import React, { FC, ReactNode, useEffect } from 'react'
import ReactHtmlParser from 'react-html-parser'
import { formatDistance } from 'date-fns'
import clsx from 'clsx'
import { getFirstLetterFromName } from '@pepper/utils/src/function/misc'

import { Avatar, Attachments } from '@pepper/ui'

import styles from './_.module.css'

interface Props {
  data: ThreadMessage
  lastMessageInThread: boolean
  scrollToBottom: () => void
}

const Thread: FC<Props> = ({ data, lastMessageInThread, scrollToBottom }) => {
  useEffect(() => {
    lastMessageInThread && scrollToBottom()
  }, [lastMessageInThread, scrollToBottom])

  const getName = () => {
    const firstName = data?.sender?.first_name || ''
    const lastName = data?.sender?.last_name || ''
    return getFirstLetterFromName(firstName, lastName)
  }

  const renderMessageParts = (text_parts: ThreadTextPart[]): ReactNode => {
    return text_parts.map((item, index) => {
      if (item.type === 'mention')
        return (
          <span className={styles.highlight} key={index}>
            &#64;
            {`${item.meta.mentioned_user?.first_name ?? ''} ${
              item.meta.mentioned_user?.last_name ?? ''
            }`}
          </span>
        )
      else if (item.type === 'text') return <span key={index}>{item.text}</span>
      else if (item.type === 'html') return ReactHtmlParser(item.text)
      else return ''
    })
  }

  const userName = getName()
  return (
    <div
      className={clsx(styles.threadContainer, {
        [styles.highlightLastThread]: data.unread,
      })}
    >
      <div className={styles.profilePic}>
        <Avatar
          className={styles.avatar}
          nameChar={userName}
          src={data.sender.avatar}
        >
          {userName}
        </Avatar>
      </div>
      <div className={styles.threadDetails}>
        <div className={styles.userDetails}>
          <span className={styles.name}>
            {`${data.sender.first_name} ${data.sender.last_name}`}
          </span>
          <span className={styles.threadTimestamp}>
            {formatDistance(new Date(), new Date(data.timestamp))} ago
          </span>
        </div>
        {data.text ? (
          <div className={styles.message}>
            <pre>{renderMessageParts(data.text_parts)}</pre>
          </div>
        ) : (
          ''
        )}
        {data?.attachments && data?.attachments.length > 0 ? (
          <Attachments attachments={data.attachments} />
        ) : (
          ''
        )}
      </div>
    </div>
  )
}

export default Thread
